import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LanguageContext, getTranslation } from '../LanguageContext';
import { FaTags, FaVideo, FaImages, FaBuilding, FaNewspaper, FaSpinner, FaShareAlt, FaEye, FaYoutube, FaFilm } from 'react-icons/fa';
import './SectionsMenu.css';

const LOGO_URL = '/image/logo.png';

const sectionsTranslations = {
  en: {
    sectionTitle: "Explore Our Sections",
    sectionSubtitle: "Discover all the content and services we offer",
    activities: "Activities",
    activitiesDesc: "Our events, programs and community activities",
    videos: "Videos",
    videosDesc: "Watch our video library and media content",
    photos: "Photos",
    photosDesc: "Browse our photo albums and galleries",
    depositStations: "Deposit Stations",
    depositStationsDesc: "Our deposit and reception stations",
    newspapers: "Newspapers",
    newspapersDesc: "Read our latest newspaper articles",
    loading: "Loading...",
    noData: "No data available yet",
    images: "images",
    videosCount: "videos",
    photosCount: "photos",
    location: "Location",
    imageOf: "Image {current} of {total}",
    viewAlbum: "View Album",
    share: "Share",
    back: "Back",
    photoCount: "{count} photos",
    videoCount: "{count} videos",
    audioCount: "{count} audios",
    pageName: "ANPE Maroc",
    readArticle: "Read Article",
    youtubeVideos: "YouTube Videos",
    regularVideos: "Regular Videos",
    youtubeVideosDesc: "Watch our educational YouTube videos",
    regularVideosDesc: "Browse our uploaded video library",
    chooseVideoType: "Choose video type"
  },
  fr: {
    sectionTitle: "Explorez Nos Sections",
    sectionSubtitle: "Découvrez tout le contenu et les services que nous offrons",
    activities: "Activités",
    activitiesDesc: "Nos événements, programmes et activités communautaires",
    videos: "Nos émissions télé",
    videosDesc: "Regardez notre vidéothèque et contenu médiatique",
    photos: "Photos",
    photosDesc: "Parcourez nos albums photos et galeries",
    depositStations: "Nos émissions radio",
    depositStationsDesc: "Nos Nos émissions radio et d'accueil",
    newspapers: "Journaux",
    newspapersDesc: "Lisez nos derniers articles de presse",
    loading: "Chargement...",
    noData: "Aucune donnée disponible pour le moment",
    images: "images",
    videosCount: "vidéos",
    photosCount: "photos",
    location: "Emplacement",
    imageOf: "Image {current} de {total}",
    viewAlbum: "Voir l'album",
    share: "Partager",
    back: "Retour",
    photoCount: "{count} photos",
    videoCount: "{count} vidéos",
    audioCount: "{count} audios",
    pageName: "ANPE Maroc",
    readArticle: "Lire l'article",
    youtubeVideos: "Vidéos YouTube",
    regularVideos: "Vidéos ordinaires",
    youtubeVideosDesc: "Regardez nos vidéos éducatives sur YouTube",
    regularVideosDesc: "Parcourez notre vidéothèque uploadée",
    chooseVideoType: "Choisissez le type de vidéo"
  },
  ar: {
    sectionTitle: "استكشف أقسامنا",
    sectionSubtitle: "اكتشف جميع المحتويات والخدمات التي نقدمها",
    activities: "الأنشطة",
    activitiesDesc: "فعالياتنا وبرامجنا وأنشطتنا المجتمعية",
    videos: "الفيديوهات",
    videosDesc: "شاهد مكتبة الفيديو والمحتوى الإعلامي",
    photos: "الصور",
    photosDesc: "تصفح ألبومات الصور والمعارض",
    depositStations: "محطات إيداعية",
    depositStationsDesc: "محطات الإيداع والاستقبال الخاصة بنا",
    newspapers: "الجرائد",
    newspapersDesc: "اقرأ آخر مقالاتنا الصحفية",
    loading: "جاري التحميل...",
    noData: "لا توجد بيانات متاحة حالياً",
    images: "صور",
    videosCount: "فيديوهات",
    photosCount: "صور",
    location: "الموقع",
    imageOf: "صورة {current} من {total}",
    viewAlbum: "عرض الألبوم",
    share: "مشاركة",
    back: "رجوع",
    photoCount: "{count} صور",
    videoCount: "{count} فيديوهات",
    audioCount: "{count} ملفات صوتية",
    pageName: "ANPE المغرب",
    readArticle: "قراءة المقال",
    youtubeVideos: "فيديوهات يوتيوب",
    regularVideos: "فيديوهات عادية",
    youtubeVideosDesc: "شاهد فيديوهاتنا التعليمية على يوتيوب",
    regularVideosDesc: "تصفح مكتبة الفيديوهات المرفوعة",
    chooseVideoType: "اختر نوع الفيديو"
  }
};

const SectionsMenu = () => {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const translate = (key) => getTranslation(sectionsTranslations, language, key);
  const isRTL = language === 'ar';

  const [activeTab, setActiveTab] = useState(null);
  const [videoSubTab, setVideoSubTab] = useState(null); // 'youtube' or 'regular'
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const contentRef = useRef(null);

  const tabs = [
    { key: 'activities', icon: <FaTags />, color: '#1a5276', gradient: 'linear-gradient(135deg, #1a5276, #2e86c1)', endpoint: '/activities' },
    { key: 'videos', icon: <FaVideo />, color: '#c0392b', gradient: 'linear-gradient(135deg, #c0392b, #e74c3c)', endpoint: '/videos' },
    { key: 'photos', icon: <FaImages />, color: '#0d6931', gradient: 'linear-gradient(135deg, #0d6931, #27ae60)', endpoint: '/photos' },
    { key: 'depositStations', icon: <FaBuilding />, color: '#6c3483', gradient: 'linear-gradient(135deg, #6c3483, #8e44ad)', endpoint: '/deposit-stations' },
    { key: 'newspapers', icon: <FaNewspaper />, color: '#d35400', gradient: 'linear-gradient(135deg, #d35400, #e67e22)', endpoint: '/newspapers' },
  ];

  const handleTabClick = (tabKey) => {
    if (activeTab === tabKey) {
      setActiveTab(null);
      setVideoSubTab(null);
      setData([]);
      return;
    }
    setActiveTab(tabKey);
    if (tabKey !== 'videos') {
      setVideoSubTab(null);
    } else {
      setVideoSubTab('youtube');
    }
  };

  const handleVideoSubTab = (subTab) => {
    if (videoSubTab === subTab) return;
    setVideoSubTab(subTab);
  };

  useEffect(() => {
    if (!activeTab) return;
    // If videos tab is active but no sub-tab selected, don't fetch
    if (activeTab === 'videos' && !videoSubTab) return;

    const tab = tabs.find(t => t.key === activeTab);
    if (!tab) return;

    let endpoint = tab.endpoint;
    if (activeTab === 'videos' && videoSubTab === 'youtube') {
      endpoint = '/youtube-videos';
    } else if (activeTab === 'videos' && videoSubTab === 'regular') {
      endpoint = '/videos';
    }

    const fetchData = async () => {
      setLoading(true);
      setData([]);
      try {
        const res = await axios.get(`https://anpe-web-api.ddns.net/api2${endpoint}`);
        setData(res.data.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setData([]);
      } finally {
        setLoading(false);
        // Auto-scroll to content on mobile
        if (window.innerWidth <= 768) {
          setTimeout(() => {
            if (contentRef.current) {
              contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 150);
        }
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [activeTab, videoSubTab]);

  const activeTabObj = tabs.find(t => t.key === activeTab);

  // ========== Helpers ==========

  const getItemThumb = (item) => {
    // For images/photos
    if (item.images && item.images.length > 0) return item.images[0];
    if (item.URLImages && item.URLImages.length > 0) return item.URLImages[0];
    
    // For videos - use thumbnail from first video
    if (item.Videos && item.Videos.length > 0) {
      const firstVideo = item.Videos[0];
      if (firstVideo.URLImage && firstVideo.URLImage !== '') {
        return firstVideo.URLImage;
      }
      // If no thumbnail, return default video placeholder
      return 'https://anpe-web-api.ddns.net/api2/uploads/default-video-thumb.svg';
    }
    
    // For newspapers - use the image
    if (item.image) return item.image;
    
    return null;
  };

  const getItemImages = (item) => {
    if (item.images && item.images.length > 0) return item.images;
    if (item.URLImages && item.URLImages.length > 0) return item.URLImages;
    return [];
  };

  // ========== RENDER: Deposit Station Cards (Image + Audio) ==========

  const renderStationCards = () => {
    // Flatten all stations' image+audio pairs into one list
    const allPairs = [];
    data.forEach((item) => {
      const images = item.images || [];
      const audios = item.audios || [];
      const maxLen = Math.max(images.length, audios.length);
      for (let i = 0; i < maxLen; i++) {
        allPairs.push({
          itemId: item._id,
          title: item.title,
          image: images[i] || null,
          audio: audios[i] || null,
        });
      }
    });

    return (
      <div className="fb-posts-feed">
        {allPairs.map((pair, idx) => (
          <div key={idx} className="fb-post-card">
            {/* Post Header */}
            <div className="fb-post-header">
              <div className="fb-post-avatar">
                <img src={LOGO_URL} alt="ANPE" onError={(e) => { e.target.style.display = 'none'; }} />
                <div className="fb-post-avatar-fallback">{activeTabObj?.icon}</div>
              </div>
              <div className="fb-post-author">
                <span className="fb-post-page-name">{translate('pageName')}</span>
                <span className="fb-post-title">{pair.title}</span>
              </div>
            </div>

            {/* Image */}
            {pair.image && (
              <div className="fb-station-image" onClick={() => navigate(`/detail/${activeTab}/${pair.itemId}`)}>
                <img src={pair.image} alt={pair.title} loading="lazy" />
              </div>
            )}

            {/* Audio */}
            {pair.audio && (
              <div className="fb-station-audio">
                <span className="fb-station-audio-icon">🎙️</span>
                <audio controls>
                  <source src={pair.audio} />
                </audio>
              </div>
            )}

            {/* Post Footer */}
            <div className="fb-post-footer">
              <button className="fb-post-btn fb-btn-share">
                <FaShareAlt /> {translate('share')}
              </button>
              <button className="fb-post-btn fb-btn-view" onClick={() => navigate(`/detail/${activeTab}/${pair.itemId}`)}>
                <FaEye /> {translate('viewAlbum')}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ========== RENDER: Facebook-Style Post Cards ==========

  const renderPostCards = () => (
    <div className="fb-posts-feed">
      {data.map((item) => {
        const thumb = getItemThumb(item);
        const imgCount = getItemImages(item).length;
        const vidCount = (item.videos ? item.videos.length : 0) + (item.Videos ? item.Videos.length : 0);

        return (
          <div key={item._id} className="fb-post-card">
            {/* Post Header: Logo + Page name + Title */}
            <div className="fb-post-header">
              <div className="fb-post-avatar">
                <img src={LOGO_URL} alt="ANPE" onError={(e) => { e.target.style.display = 'none'; }} />
                <div className="fb-post-avatar-fallback">{activeTabObj?.icon}</div>
              </div>
              <div className="fb-post-author">
                <span className="fb-post-page-name">{translate('pageName')}</span>
                <span className="fb-post-title">{item.title}</span>
              </div>
            </div>

            {/* Cover Photo */}
            <div className="fb-post-cover" onClick={() => navigate(`/detail/${activeTab}/${item._id}`)}>
              {thumb ? (
                <img src={thumb} alt={item.title} loading="lazy" />
              ) : (
                <div className="fb-post-placeholder">{activeTabObj?.icon}</div>
              )}
              {/* Count Badge */}
              {imgCount > 0 && (
                <div className="fb-post-badge">
                  {translate('photoCount').replace('{count}', imgCount)}
                </div>
              )}
              {vidCount > 0 && imgCount === 0 && (
                <div className="fb-post-badge">
                  {translate('videoCount').replace('{count}', vidCount)}
                </div>
              )}
            </div>

            {/* Post Footer: Partager | Voir l'album */}
            <div className="fb-post-footer">
              <button className="fb-post-btn fb-btn-share">
                <FaShareAlt /> {translate('share')}
              </button>
              <button className="fb-post-btn fb-btn-view" onClick={() => navigate(`/detail/${activeTab}/${item._id}`)}>
                <FaEye /> {translate('viewAlbum')}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ========== RENDER: YouTube Video Cards ==========
  const getYouTubeVideoId = (url) => {
    if (!url || typeof url !== 'string') return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const renderYouTubeCards = () => (
    <div className="fb-posts-feed">
      {data.map((video) => {
        const videoId = getYouTubeVideoId(video.url);
        return (
          <div key={video._id} className="fb-post-card">
            <div className="fb-post-header">
              <div className="fb-post-avatar">
                <img src={LOGO_URL} alt="ANPE" onError={(e) => { e.target.style.display = 'none'; }} />
                <div className="fb-post-avatar-fallback"><FaYoutube /></div>
              </div>
              <div className="fb-post-author">
                <span className="fb-post-page-name">{translate('pageName')}</span>
                <span className="fb-post-title">{video.title}</span>
              </div>
            </div>
            <div className="fb-yt-video-wrapper">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                title={video.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="fb-post-footer">
              <button className="fb-post-btn fb-btn-share">
                <FaShareAlt /> {translate('share')}
              </button>
              <button className="fb-post-btn fb-btn-view" onClick={() => window.open(video.url, '_blank')}>
                <FaYoutube /> YouTube
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ========== RENDER: Video Sub-Tabs ==========
  const renderVideoSubTabs = () => (
    <div className="video-subtabs">
      <div className="video-subtabs-header">
        <h3>{translate('chooseVideoType')}</h3>
      </div>
      <div className="video-subtabs-grid">
        <div
          className={`video-subtab-card ${videoSubTab === 'youtube' ? 'active' : ''}`}
          onClick={() => handleVideoSubTab('youtube')}
        >
          <div className="video-subtab-icon youtube-icon">
            <FaYoutube />
          </div>
          <h4>{translate('youtubeVideos')}</h4>
          <p>{translate('youtubeVideosDesc')}</p>
        </div>
        <div
          className={`video-subtab-card ${videoSubTab === 'regular' ? 'active' : ''}`}
          onClick={() => handleVideoSubTab('regular')}
        >
          <div className="video-subtab-icon regular-icon">
            <FaFilm />
          </div>
          <h4>{translate('regularVideos')}</h4>
          <p>{translate('regularVideosDesc')}</p>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (!activeTab) return null;

    // Videos tab: show sub-tabs first
    if (activeTab === 'videos') {
      return (
        <>
          {renderVideoSubTabs()}
          {videoSubTab && loading && (
            <div className="sm-loading">
              <FaSpinner className="sm-spin" />
              <p>{translate('loading')}</p>
            </div>
          )}
          {videoSubTab && !loading && data.length === 0 && (
            <div className="sm-empty">
              <p>{translate('noData')}</p>
            </div>
          )}
          {videoSubTab && !loading && data.length > 0 && (
            videoSubTab === 'youtube' ? renderYouTubeCards() : renderPostCards()
          )}
        </>
      );
    }

    if (loading) {
      return (
        <div className="sm-loading">
          <FaSpinner className="sm-spin" />
          <p>{translate('loading')}</p>
        </div>
      );
    }
    if (data.length === 0) {
      return (
        <div className="sm-empty">
          <p>{translate('noData')}</p>
        </div>
      );
    }
    if (activeTab === 'depositStations') return renderStationCards();
    return renderPostCards();
  };

  return (
    <section className={`sections-menu ${isRTL ? 'rtl' : ''}`} id="sections-menu">
      <div className="sections-menu-container">
        <div className="sections-menu-header">
          <h2>{translate('sectionTitle')}</h2>
          <p>{translate('sectionSubtitle')}</p>
        </div>

        {/* Tab Cards */}
        <div className="sections-menu-grid">
          {tabs.map((tab) => (
            <div
              key={tab.key}
              className={`section-menu-card ${activeTab === tab.key ? 'active' : ''}`}
              style={{ '--card-color': tab.color, '--card-gradient': tab.gradient }}
              onClick={() => handleTabClick(tab.key)}
            >
              <div className="section-menu-icon">
                {tab.icon}
              </div>
              <h3>{translate(tab.key)}</h3>
              <p>{translate(tab.key + 'Desc')}</p>
            </div>
          ))}
        </div>

        {/* Inline Content */}
        {activeTab && (
          <div
            className="sm-content-area"
            ref={contentRef}
            style={{ '--active-color': activeTabObj?.color, '--active-gradient': activeTabObj?.gradient }}
          >
            <div className="sm-content-bar" />
            {renderContent()}
          </div>
        )}
      </div>

    </section>
  );
};

export default SectionsMenu;
